import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function CreditApplication() {
  const { formatApiError } = useAuth();
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    requested_amount: 50000, purpose: "Working capital",
    annual_revenue: 1200000, years_in_business: 3,
    credit_score_band: "good",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/credit/me").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        requested_amount: parseFloat(form.requested_amount),
        annual_revenue: parseFloat(form.annual_revenue),
        years_in_business: parseFloat(form.years_in_business),
      };
      const { data } = await api.post("/credit/apply", payload);
      toast.success(`Decision: ${data.decision.toUpperCase()} — score ${data.composite_score}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="credit-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[#3B82F6]">Financing</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Credit Application</h1>
      <p className="text-slate-500 mt-2 max-w-xl">Apply for a working-capital line. Decision in seconds.</p>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <form onSubmit={submit} className="sk-card p-6 space-y-4" data-testid="credit-form">
          <FormInput label="Requested amount (USD)" type="number" required value={form.requested_amount} onChange={upd("requested_amount")} testid="credit-amount" />
          <FormInput label="Purpose" required value={form.purpose} onChange={upd("purpose")} testid="credit-purpose" />
          <FormInput label="Annual revenue (USD)" type="number" required value={form.annual_revenue} onChange={upd("annual_revenue")} testid="credit-revenue" />
          <FormInput label="Years in business" type="number" step="0.5" required value={form.years_in_business} onChange={upd("years_in_business")} testid="credit-tenure" />
          <div>
            <Label className="text-slate-600">Credit score band</Label>
            <Select value={form.credit_score_band} onValueChange={(v) => setForm({ ...form, credit_score_band: v })}>
              <SelectTrigger className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid="credit-band-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-slate-200 text-slate-900">
                <SelectItem value="excellent">Excellent (740+)</SelectItem>
                <SelectItem value="good">Good (670-739)</SelectItem>
                <SelectItem value="fair">Fair (580-669)</SelectItem>
                <SelectItem value="poor">Poor (&lt;580)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11" data-testid="credit-submit-btn">
            Submit Application
          </Button>
        </form>

        <div>
          <h3 className="font-heading text-lg mb-3">Decision History</h3>
          {history.length === 0 && <div className="sk-card p-6 text-slate-400 text-sm">No applications yet.</div>}
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.credit_app_id} className="sk-card p-5" data-testid={`credit-row-${h.credit_app_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Requested</div>
                    <div className="font-heading text-xl">${Number(h.requested_amount).toLocaleString()}</div>
                  </div>
                  <DecisionPill d={h.decision} />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400">Score:</span> <span className="font-mono">{h.composite_score}</span></div>
                  <div><span className="text-slate-400">Approved:</span> <span className="font-mono">${Number(h.approved_amount || 0).toLocaleString()}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FormInput({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-slate-600">{label}</Label>
      <Input {...rest} className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid={testid} />
    </div>
  );
}

function DecisionPill({ d }) {
  const map = {
    approved: { c: "#10B981", l: "Approved" },
    manual_review: { c: "#F59E0B", l: "Review" },
    declined: { c: "#FF4757", l: "Declined" },
  };
  const it = map[d] || { c: "#71717A", l: d };
  return (
    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider"
      style={{ background: `${it.c}1A`, color: it.c, border: `1px solid ${it.c}33` }}>
      {it.l}
    </span>
  );
}
